import { apiClient } from './client';
import type { ApiResponse } from '../types';

export interface AdminPlanet {
  planet_id: string;
  owner_user_id: string;
  name: string;
  stage: number;
  created_at: string;
  updated_at: string;
  user: { user_id: string; name: string; email: string; role: string };
  universe: { name: string; universe_type: string };
  _count: { upgrade_instances: number; surface_items: number };
  balance: number;
}

export interface AdminPlanetDetail {
  planet: AdminPlanet & {
    surface_items: unknown[];
    upgrade_instances: unknown[];
    development_projects: unknown[];
  };
  balance: number;
}

export interface AdminUniverse {
  universe_id: string;
  name: string;
  universe_type: string;
  paused: boolean;
  capacity: number;
  archived: boolean;
  archivedAt?: string | null;
  signupsEnabled: boolean;
  leaderboardEnabled: boolean;
  visibilityEnabled: boolean;
  join_code?: string | null;
  _count: { planets: number };
  socialHealth: {
    signals: number;
    missions: number;
    relations: number;
    averageTrust: number;
    averageTension: number;
  };
}

export interface AdminUniverseRosterEntry {
  planet: AdminPlanet;
  user: AdminPlanet['user'] & { active: boolean };
  balance: { current_balance: number; lifetime_earned: number; lifetime_spent: number };
}

export interface AdminUniverseRoster {
  universe: AdminUniverse & { planets: AdminPlanet[] };
  roster: AdminUniverseRosterEntry[];
}

export interface AdminUpgrade {
  upgrade_definition_id: string;
  name: string;
  description: string | null;
  category: string;
  stage_requirement: number;
  cost: number;
  prerequisites: { id: string; name: string }[];
  effects: Record<string, unknown>;
  placement_rules: Record<string, unknown>;
  unlock_rules: Record<string, unknown>;
  repeatability: string;
  max_repeats: number;
  active: boolean;
  construction_days: number;
  daily_effects: Record<string, unknown>;
  completion_effects: Record<string, unknown>;
  upkeep_cost: number;
  conflict_group: string | null;
  synergy_with: string[];
  flavour_text: string | null;
}

export interface AdminUpgradeInput {
  name?: string;
  description?: string;
  category?: string;
  stage_requirement?: number;
  cost?: number;
  prerequisites?: string[];
  effects?: Record<string, unknown>;
  placement_rules?: Record<string, unknown>;
  unlock_rules?: Record<string, unknown>;
  repeatability?: string;
  max_repeats?: number;
  active?: boolean;
  construction_days?: number;
  daily_effects?: Record<string, unknown>;
  completion_effects?: Record<string, unknown>;
  upkeep_cost?: number;
  conflict_group?: string | null;
  synergy_with?: string[];
  flavour_text?: string;
}

export const adminApi = {
  login: (username: string, password: string) =>
    apiClient
      .post<ApiResponse<{ token: string }>>('/admin/login', { username, password })
      .then((r) => r.data),

  getPlanets: () =>
    apiClient
      .get<ApiResponse<AdminPlanet[]>>('/admin/planets')
      .then((r) => r.data),

  getPlanet: (id: string) =>
    apiClient
      .get<ApiResponse<AdminPlanetDetail>>(`/admin/planets/${id}`)
      .then((r) => r.data),

  adjustCredits: (planetId: string, amount: number) =>
    apiClient
      .post<ApiResponse<{ account: { current_balance: number }; transaction: unknown }>>(
        `/admin/planets/${planetId}/credits`,
        { amount },
      )
      .then((r) => r.data),

  getUniverses: (archived: 'false' | 'true' | 'all' = 'false') =>
    apiClient
      .get<ApiResponse<AdminUniverse[]>>('/admin/universes', { params: { archived } })
      .then((r) => r.data),

  createUniverse: (name: string, capacity: number) =>
    apiClient
      .post<ApiResponse<AdminUniverse>>('/admin/universes', { name, capacity })
      .then((r) => r.data),

  updateUniverse: (id: string, data: { paused?: boolean; archive?: boolean; resetSocial?: boolean; name?: string; capacity?: number; signupsEnabled?: boolean; leaderboardEnabled?: boolean; visibilityEnabled?: boolean }) =>
    apiClient
      .patch<ApiResponse<AdminUniverse>>(`/admin/universes/${id}`, data)
      .then((r) => r.data),

  getUniverseRoster: (id: string) =>
    apiClient
      .get<ApiResponse<AdminUniverseRoster>>(`/admin/universes/${id}/roster`)
      .then((r) => r.data),

  getUpgrades: () =>
    apiClient
      .get<ApiResponse<AdminUpgrade[]>>('/admin/upgrades')
      .then((r) => r.data),

  updateUpgrade: (id: string, data: AdminUpgradeInput) =>
    apiClient
      .patch<ApiResponse<AdminUpgrade>>(`/admin/upgrades/${id}`, data)
      .then((r) => r.data),

  createUpgrade: (data: AdminUpgradeInput) =>
    apiClient
      .post<ApiResponse<AdminUpgrade>>('/admin/upgrades', data)
      .then((r) => r.data),
};

export function getAdminToken(): string | null {
  return localStorage.getItem('admin_token');
}

export function setAdminToken(token: string) {
  localStorage.setItem('admin_token', token);
}

export function clearAdminToken() {
  localStorage.removeItem('admin_token');
}
